import type { FormConfig, Journey, PixelConfig, Touchpoint } from './types';
import { getAttribution, getJourneyString, getTimestampString } from './journey';
import { log } from './utils';

declare const __PIXEL_VERSION__: string;

let beaconHealth: 'ok' | 'disabled' | 'error' = 'disabled';

export function getBeaconHealth(): 'ok' | 'disabled' | 'error' {
  return beaconHealth;
}

/* ─────────────── dataLayer / gtag ─────────────── */

function pushDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
  log('dataLayer.push', payload);
}

export function pushPixelLoaded(config: PixelConfig): void {
  pushDataLayer({
    event: 'tdg_pixel_loaded',
    pixel_version: __PIXEL_VERSION__,
    pixel_property: config.property,
  });
}

export function pushTouchpoint(touchpoint: Touchpoint, journey: Journey, config: PixelConfig): void {
  pushDataLayer({
    event: 'tdg_attribution_touchpoint',
    attribution_source: touchpoint.source,
    journey_length: journey.touchpoints.length,
    first_touch: journey.touchpoints[0]?.source ?? '',
    last_touch: touchpoint.source,
    attribution_model: config.attributionModel,
  });
  updateGA4UserProperties(journey, config);
}

export function pushFormSubmission(form: FormConfig, journey: Journey, config: PixelConfig): void {
  const attribution = getAttribution(journey, config.attributionModel);
  const journeyDays = computeJourneyDays(journey);

  pushDataLayer({
    event: 'generate_lead',
    form_type: form.id,
    form_name: form.description,
    attribution_source: attribution,
    first_touch: journey.touchpoints[0]?.source ?? '',
    last_touch: journey.touchpoints[journey.touchpoints.length - 1]?.source ?? '',
    full_journey: getJourneyString(journey),
    journey_timestamps: getTimestampString(journey),
    journey_length: journey.touchpoints.length,
    journey_days: journeyDays,
    method: attribution,
  });

  beaconFormSubmission(form, journey, config);
}

function computeJourneyDays(journey: Journey): number {
  if (journey.touchpoints.length < 2) return 0;
  return Math.round(((journey.lastSeen - journey.firstSeen) / 86_400_000) * 10) / 10;
}

function updateGA4UserProperties(journey: Journey, config: PixelConfig): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('set', 'user_properties', {
      attribution_journey: getJourneyString(journey),
      attribution_first_touch: journey.touchpoints[0]?.source ?? '',
      attribution_last_touch: journey.touchpoints[journey.touchpoints.length - 1]?.source ?? '',
      attribution_journey_length: journey.touchpoints.length,
      attribution_model: config.attributionModel,
    });
  } catch (e) {
    log('gtag set failed', e);
  }
}

/* ─────────────── Server-side beacon (v2) ─────────────── */

function beaconAvailable(config: PixelConfig): boolean {
  return !!config.beaconEndpoint && typeof navigator !== 'undefined' && !!navigator.sendBeacon;
}

export function beaconTouchpoint(touchpoint: Touchpoint, config: PixelConfig): void {
  if (!beaconAvailable(config)) return;
  try {
    const blob = new Blob(
      [JSON.stringify({ type: 'touchpoint', property: config.property, ...touchpoint })],
      { type: 'application/json' }
    );
    const ok = navigator.sendBeacon(config.beaconEndpoint!, blob);
    beaconHealth = ok ? 'ok' : 'error';
  } catch (e) {
    beaconHealth = 'error';
    log('beacon touchpoint failed', e);
  }
}

export function beaconFormSubmission(form: FormConfig, journey: Journey, config: PixelConfig): void {
  if (!beaconAvailable(config)) return;
  try {
    const blob = new Blob(
      [
        JSON.stringify({
          type: 'submission',
          property: config.property,
          form: form.id,
          journey,
        }),
      ],
      { type: 'application/json' }
    );
    const ok = navigator.sendBeacon(config.beaconEndpoint!, blob);
    beaconHealth = ok ? 'ok' : 'error';
  } catch (e) {
    beaconHealth = 'error';
    log('beacon submission failed', e);
  }
}
